/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'

import useT from '@/i18n/useT'

type UnverifyFeedbackDialogProps = {
  open: boolean
  loading: boolean
  subjectLabel: string
  onClose: () => void
  onSubmit: (feedbackNote: string | null) => Promise<unknown> | unknown
}

function UnverifyFeedbackDialog({
  open,
  loading,
  subjectLabel,
  onClose,
  onSubmit,
}: UnverifyFeedbackDialogProps) {
  const { t } = useT()
  const [note, setNote] = React.useState('')

  React.useEffect(() => {
    if (!open) setNote('')
  }, [open])

  const trimmedNote = note.trim()

  const handleSubmit = (feedbackNote: string | null) => {
    void onSubmit(feedbackNote)
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {t('review.unverifyFeedback.title', 'Send back with feedback?')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('review.unverifyFeedback.body', {
              subject: subjectLabel,
              defaultValue:
                'You can send this item back to the original labeler with a note. It will appear at the top of their queue the next time they work on this task. Leave the note blank to send it to the general queue.',
            })}
          </Typography>
          <TextField
            label={t('review.unverifyFeedback.noteLabel', 'Feedback note')}
            placeholder={t(
              'review.unverifyFeedback.notePlaceholder',
              'Optional feedback for the labeler',
            )}
            value={note}
            onChange={event => setNote(event.target.value)}
            multiline
            minRows={4}
            disabled={loading}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant={trimmedNote ? 'outlined' : 'contained'}
          color="warning"
          onClick={() => handleSubmit(null)}
          disabled={loading}
        >
          {t('review.unverifyFeedback.unverifyGeneral', 'Unverify')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => handleSubmit(trimmedNote)}
          disabled={loading || !trimmedNote}
        >
          {t(
            'review.unverifyFeedback.sendWithFeedback',
            'Unverify with feedback',
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UnverifyFeedbackDialog
